/** @jsxImportSource @emotion/react */
import dayjs, { Dayjs } from 'dayjs'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { Autocomplete, Box, Slider, TextField, Typography, useTheme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import apiClient from '@app/config/axios.config'
import type { MapLayer } from '@app/types/layerTypes'
import { Button } from '@app/components/Atoms/Button'
import { Icon } from '@app/components/Atoms/Icon'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import { formatNumberWithCommas } from '@app/utils/numbers.utils'
import { ElementTypeFilters } from '@app/types/filterTypes'
import { EmptySection } from '../../RiskSideDrawerContent/components/EmptySection'
import { layerDetailsHeader } from '../../RiskSideDrawerContent/tabs/InformativePanel/LayersTab/InfoAccordions/LayerInfo/LayerInfo.styles'
import { elementFilterForm } from '../LayersControl.styles'
import { useRiskFilterState } from '@app/risk-migration/hooks/useRiskFilterState'
import {
  filterKeyValuesAtom,
  filterKeyValuesCacheAtom,
  layerLoadingAtom,
} from '@app/risk-migration/atoms/riskLayers.atoms'

// stable "nothing selected" value to fix MUI autocomplete reset
const NO_FILTER_VALUES: string[] = []

interface ElementFilterFormProps {
  layer: MapLayer
}

// example format of layer.filterable_information:
// "filterable_information": [
//   {
//     "key": "Orchard",
//     "Label": "Orchard",
//     "type": "text",
//     "lookup": true
//   },
//   {
//     "key": "Trees",
//     "Label": "Trees",
//     "type": "numeric"
//   }
// ],

export const ElementFilterForm = function ElementFilterForm({ layer }: ElementFilterFormProps) {
  const theme = useTheme()
  const currencySymbol = useAtomValue(currencySymbolAtom)
  const { elementFilters, setElementFilters } = useRiskFilterState()
  const [filterKeyValues, setfilterKeyValues] = useAtom(filterKeyValuesAtom)
  const [filterKeyValuesCache, setFilterKeyValuesCache] = useAtom(filterKeyValuesCacheAtom)
  const [layerLoading, setLayerLoading] = useAtom(layerLoadingAtom)
  const [filterValues, setFilterValues] = useState<ElementTypeFilters>({})
  const [prevAssetType, setPrevAssetType] = useState<string | null>(null)
  const [minMaxByFilter, setMinMaxByFilter] = useState<{ [key: string]: number[] | Dayjs[] }>({})
  const [urlSuffix, setUrlSuffix] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const shouldFetch = useCallback(() => {
    if (!prevAssetType || prevAssetType === layer.type) return false
    setPrevAssetType(layer.type)
    return true
  }, [layer.type, prevAssetType])

  const createURLSuffix = useCallback(() => {
    if (!layer.filterable_information) return null

    const textFilters = layer.filterable_information.filter(
      (item) => item.type === 'text' && item.lookup,
    )
    const numericFilters = layer.filterable_information.filter((item) => item.type === 'numeric')
    const monetaryFilters = layer.filterable_information.filter((item) => item.type === 'monetary')
    const dateFilters = layer.filterable_information.filter((item) => item.type === 'date')

    const textQueryString = textFilters.length
      ? `text_keys=${textFilters.map((item) => item.key).join(',')}`
      : ''
    const numericQueryString = numericFilters.length
      ? `numeric_keys=${numericFilters.map((item) => item.key).join(',')}`
      : ''
    const monetaryQueryString = monetaryFilters.length
      ? `numeric_keys=${monetaryFilters.map((item) => item.key).join(',')}`
      : ''
    const dateQueryString = dateFilters.length
      ? `date_keys=${dateFilters.map((item) => item.key).join(',')}`
      : ''

    let totalQueryString = textQueryString ? `?${textQueryString}` : ''
    if (numericQueryString) {
      totalQueryString = totalQueryString
        ? `${totalQueryString}&${numericQueryString}`
        : `?${numericQueryString}`
    }
    if (monetaryQueryString) {
      totalQueryString = totalQueryString
        ? `${totalQueryString}&${monetaryQueryString}`
        : `?${monetaryQueryString}`
    }
    if (dateQueryString) {
      totalQueryString = totalQueryString
        ? `${totalQueryString}&${dateQueryString}`
        : `?${dateQueryString}`
    }
    if (!totalQueryString) return null
    return `${layer.type}${totalQueryString}`
  }, [layer.filterable_information, layer.type])

  // used for setting the values when swapping tabs or other changes
  useEffect(() => {
    // use existing filters for this layer type
    if (elementFilters[layer.type]) {
      setFilterValues(elementFilters[layer.type])
      // grab ranges for types that need them in order to set defaults
    } else {
      if (!filterKeyValues || !layer.filterable_information) return

      const filterKeyValuesWithRange = layer.filterable_information.filter(
        (info) => info.type === 'numeric' || info.type === 'date' || info.type === 'monetary',
      )
      const updatesToApply: ElementTypeFilters = {}

      filterKeyValuesWithRange.forEach((info) => {
        const tempUrlSuffix = createURLSuffix()
        if (!tempUrlSuffix) return
        const keyedFilteredValues = filterKeyValues?.[tempUrlSuffix]?.[info.key]

        if (keyedFilteredValues === null || !keyedFilteredValues) return

        // Convert to appropriate type based on field type
        const values =
          info.type === 'date'
            ? [keyedFilteredValues[0], keyedFilteredValues[1]]
            : [parseFloat(keyedFilteredValues[0]), parseFloat(keyedFilteredValues[1])]

        updatesToApply[info.key] = {
          filter: 'between',
          values,
        }
      })

      // Initialize lookup fields with empty arrays for multi-select
      const lookupFields = layer.filterable_information.filter((info) => info.lookup)
      lookupFields.forEach((info) => {
        if (!updatesToApply[info.key]) {
          updatesToApply[info.key] = {
            filter: 'match',
            values: [],
          }
        }
      })

      setFilterValues((prev) => {
        return {
          ...prev,
          ...updatesToApply,
        }
      })
    }
  }, [elementFilters, filterKeyValues, layer.type, createURLSuffix, layer.filterable_information])

  useEffect(() => {
    if (!layer.filterable_information || !shouldFetch) return
    setIsLoading(true)
    const abortController = new AbortController()

    const tempUrlSuffix = createURLSuffix()

    if (!tempUrlSuffix) {
      setIsLoading(false)
      return
    }

    setUrlSuffix(tempUrlSuffix)

    if (filterKeyValuesCache?.[tempUrlSuffix]) {
      const existingData = filterKeyValues?.[tempUrlSuffix]
      if (!existingData) {
        setfilterKeyValues((prev) => ({
          ...prev,
          [tempUrlSuffix]: filterKeyValuesCache[tempUrlSuffix],
        }))
      }
      setIsLoading(false)
      return
    }
    const getfilterKeyValues = async () => {
      try {
        const res = await apiClient.get(`/asset/identifying_information/${tempUrlSuffix}`, {
          signal: abortController.signal,
        })
        if (res.data) {
          setFilterKeyValuesCache((prev) => {
            return {
              ...prev,
              [tempUrlSuffix]: res.data,
            }
          })

          setfilterKeyValues((prev) => ({ ...prev, [tempUrlSuffix]: res.data }))
          setIsLoading(false)
        } else {
          const tempFilterKeyValues = filterKeyValues ?? {}
          delete tempFilterKeyValues[tempUrlSuffix]
          setfilterKeyValues(tempFilterKeyValues)
          setIsLoading(false)
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: Error | any) {
        if (e.code === 'ERR_CANCELED') return
        const tempFilterKeyValues = filterKeyValues ?? {}
        delete tempFilterKeyValues[tempUrlSuffix]
        setfilterKeyValues(tempFilterKeyValues)
        setIsLoading(false)
      }
    }
    getfilterKeyValues()

    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, shouldFetch])

  // set the range of the slider to the min and max values of the filterKeyValues by setting the formvalue to an array of the min and max values
  useEffect(() => {
    if (!filterKeyValues || !layer.filterable_information) return
    const filterKeyValuesWithRange = layer.filterable_information.filter(
      (info) => info.type === 'numeric' || info.type === 'date' || info.type === 'monetary',
    )
    if (!filterKeyValuesWithRange || filterKeyValuesWithRange.length === 0) return

    // set the range of the slider to the min and max values of the filterKeyValues by setting the formvalue to an array of the min and max values
    const minMax = filterKeyValuesWithRange.reduce(
      (acc, info) => {
        const keyedFilteredValues = filterKeyValues?.[urlSuffix]?.[info.key]

        if (keyedFilteredValues === null || !keyedFilteredValues) return acc

        if (info.type === 'numeric' || info.type === 'monetary') {
          acc[info.key] = [parseFloat(keyedFilteredValues[0]), parseFloat(keyedFilteredValues[1])]
        } else if (info.type === 'date') {
          acc[info.key] = [dayjs(keyedFilteredValues[0]), dayjs(keyedFilteredValues[1])]
        }
        return acc
      },
      {} as { [key: string]: number[] | Dayjs[] },
    )

    // Only update if something actually changed
    const hasChanged =
      Object.keys(minMax).length !== Object.keys(minMaxByFilter).length ||
      Object.keys(minMax).some((key) => {
        const existing = minMaxByFilter[key]
        const newVal = minMax[key]
        if (!existing && newVal) return true
        if (existing && !newVal) return true
        if (!existing && !newVal) return false
        return JSON.stringify(existing) !== JSON.stringify(newVal)
      })

    if (hasChanged) {
      setMinMaxByFilter(minMax)
    }
  }, [filterKeyValues, layer, urlSuffix, minMaxByFilter])

  const clearFilters = () => {
    const filteredElementFilters = { ...elementFilters }
    delete filteredElementFilters[layer.type]
    setElementFilters(filteredElementFilters ?? {})
    setFilterValues({})
  }

  const updateFormDateValue = (key: string, value: string, index: number) => {
    const formattedValue = dayjs(value).format('YYYY-MM-DD')
    const currentValues = (filterValues[key]?.values as string[]) || []
    if (index === 0) {
      updateFormValue(key, [formattedValue, currentValues[1] || formattedValue], 'between')
    } else if (index === 1) {
      updateFormValue(key, [currentValues[0] || formattedValue, formattedValue], 'between')
    }
  }

  const updateFormValue = (
    key: string,
    value: string | number[] | string[],
    filterType: 'match' | 'between' = 'match',
  ) => {
    setFilterValues((prev) => {
      const prevClone = { ...prev }

      // If value is empty, remove the filter
      if (Array.isArray(value) && value.length === 0) {
        delete prevClone[key]
        return prevClone
      }
      if (!value || value === '') {
        delete prevClone[key]
        return prevClone
      }

      // Format values based on filter type
      let formattedValue: string[] | number[]

      if (filterType === 'between') {
        // Between filters: keep numbers as numbers, dates as strings
        formattedValue = Array.isArray(value)
          ? typeof value[0] === 'number'
            ? (value as number[])
            : (value as string[])
          : [value]
      } else {
        // Match filters: always strings
        formattedValue = Array.isArray(value) ? value.map((val) => `${val}`) : [`${value}`]
      }

      return {
        ...prevClone,
        [key]: {
          filter: filterType,
          values: formattedValue,
        },
      }
    })
  }

  const applyFilters = () => {
    setLayerLoading(true)
    setElementFilters((prev) => {
      return {
        ...prev,
        [layer.type]: filterValues,
      }
    })
  }

  if (isLoading) {
    return <EmptySection isLoading={isLoading} title={'Loading...'} />
  }

  if (!layer.filterable_information || !filterKeyValues || !Object.keys(filterKeyValues).length)
    return null

  return (
    <Box css={elementFilterForm()}>
      <Box css={layerDetailsHeader({ theme })} sx={{ marginBottom: theme.spacing(1) }}>
        <Icon iconName={'s3://filter'} size="medium" colour={theme.palette.primary.main} />
        <h4>Filter by:</h4>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: theme.spacing(4),
          margin: theme.spacing(3),
        }}
      >
        {layer.filterable_information.map((filterable, index) => {
          return (
            <Box
              key={index}
              sx={{
                alignSelf: 'center',
                minWidth: '233.5px',
              }}
            >
              {filterable.type === 'text' && !filterable.lookup && (
                <TextField
                  type="text"
                  label={filterable.label}
                  value={filterValues[filterable.key]?.values?.[0] ?? ''}
                  onChange={(e) => updateFormValue(filterable.key, e.target.value)}
                />
              )}
              {/* has a min max like numeric */}
              {filterable.type === 'date' && filterValues[filterable.key]?.values && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: theme.spacing(4),
                    width: '100%',
                  }}
                >
                  <DatePicker
                    label={`${filterable.label} after`}
                    value={dayjs(
                      (filterValues[filterable.key]?.values as string[])?.[0] ??
                        minMaxByFilter[filterable.key]?.[0],
                    )}
                    minDate={dayjs(minMaxByFilter[filterable.key]?.[0])}
                    maxDate={dayjs((filterValues[filterable.key]?.values as string[])?.[1])}
                    onChange={(val) => updateFormDateValue(filterable.key, `${val}`, 0)}
                  />
                  <DatePicker
                    label={`${filterable.label} before`}
                    value={dayjs(
                      (filterValues[filterable.key]?.values as string[])?.[1] ??
                        minMaxByFilter[filterable.key]?.[1],
                    )}
                    minDate={dayjs((filterValues[filterable.key]?.values as string[])?.[0])}
                    maxDate={dayjs(minMaxByFilter[filterable.key]?.[1])}
                    onChange={(val) => updateFormDateValue(filterable.key, `${val}`, 1)}
                  />
                </Box>
              )}
              {(filterable.type === 'numeric' || filterable.type === 'monetary') &&
                filterValues[filterable.key]?.values && (
                  <Box
                    sx={{
                      width: '100%',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ marginLeft: `-${theme.spacing(2)}` }}>
                      {filterable.label}
                    </Typography>
                    <Slider
                      value={[
                        parseFloat(
                          `${(filterValues?.[filterable.key]?.values as (string | number)[])?.[0] ?? '0'}`,
                        ),
                        parseFloat(
                          `${(filterValues?.[filterable.key]?.values as (string | number)[])?.[1] ?? '1'}`,
                        ),
                      ]}
                      min={(minMaxByFilter[filterable.key]?.[0] ?? 0) as number}
                      max={(minMaxByFilter[filterable.key]?.[1] ?? 1) as number}
                      onChange={(_, val: number | number[]) =>
                        updateFormValue(filterable.key, Array.isArray(val) ? val : [val], 'between')
                      }
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) =>
                        filterable.type === 'numeric'
                          ? formatNumberWithCommas(value)
                          : `${currencySymbol}${formatNumberWithCommas(value)}`
                      }
                    />
                  </Box>
                )}
              {filterable.lookup &&
                filterKeyValues?.[urlSuffix]?.[filterable.key] &&
                filterKeyValues[urlSuffix][filterable.key] !== null && (
                  <Autocomplete
                    multiple
                    fullWidth
                    value={(filterValues[filterable.key]?.values ?? NO_FILTER_VALUES) as string[]}
                    onChange={(_, val) => {
                      updateFormValue(filterable.key, val as string[], 'match')
                    }}
                    options={
                      filterKeyValues[urlSuffix][filterable.key]?.map((item) =>
                        !item ? 'None of the above' : item,
                      ) ?? []
                    }
                    getOptionLabel={(option) => String(option)} // String() gets rid of the errors in the console
                    renderInput={(params) => <TextField {...params} label={filterable.label} />}
                  />
                )}
            </Box>
          )
        })}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(4),
            justifyContent: 'flex-end',
            alignSelf: 'end',
            width: '100%',
          }}
        >
          <Button variant="text" onClick={() => clearFilters()}>
            Clear Filters
          </Button>
          <Button loading={layerLoading} disabled={layerLoading} onClick={() => applyFilters()}>
            Apply
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
